import React from 'react';
import { Icon, Box, Flex, Image, Text } from '@opendoor/bricks/core';
import { InternalLink, InternalLinkProps } from 'platform/react-router';
import { useOfferContext } from 'sellDirect/OfferContext';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { LAYOUT_CONTENT_PADDING } from 'platform/components/layout/Layout';
import { Size } from '@opendoor/bricks/system/theme';

export interface IJourneyCardProps {
  /** URL to navigate to when clicking the card. */
  url: string;

  /** Target to use for the anchor. */
  target?: string;

  /** Whether or not to show the CTA (will show the chevron if false) */
  showCTA?: boolean;

  /** The message to present in the card. */
  message: React.ReactNode;

  /** The variant for the CTA. Default to 'primary-button' */
  internalLinkVariant?: InternalLinkProps['variant'];

  /** The size of the button. Will default to `sm` on mobile and `md` on desktop otherwise. */
  buttonSize?: Size;

  /**
   * This prop is used for event tracking.
   * The event name should be prefixed with journey-card
   * example: journey-card-selling-page
   */
  eventName?: string;

  /** This prop render a custom icon */
  topContent?: React.ReactNode;
}

/**
 * This card/cta prompts the seller/agent to learn more about the selling
 * process
 * @component
 */
export const JourneyCard: React.FC<IJourneyCardProps> = ({
  url,
  showCTA,
  message,
  buttonSize,
  internalLinkVariant = 'primary-button',
  eventName = 'journey-card',
  topContent,
  target,
}) => {
  const { shouldSeeAgentExperience } = useOfferContext();
  const { isMobile } = useDevice();
  const responsiveSize = isMobile ? 'sm' : 'md';
  const hasTopContent = topContent !== undefined;

  const content = (
    <Box
      backgroundColor="neutrals0"
      p={LAYOUT_CONTENT_PADDING}
      borderRadius={['semiRounded', null, 'roundedSmooth']}
      borderWidth="1px"
      borderColor="neutrals40"
      borderStyle="solid"
      data-testid="buy-journey"
    >
      <Flex
        justifyContent="space-between"
        alignItems={[showCTA ? 'flex-start' : 'center', null, 'center']}
        flexDirection={[showCTA ? 'column' : 'row', null, 'row']}
      >
        <Flex
          flexDirection={hasTopContent && !isMobile ? 'row' : 'column'}
          alignItems={hasTopContent && !isMobile ? 'center' : 'flex-start'}
          gridColumnGap={hasTopContent ? '4' : '3'}
          flexGrow={1}
          mr={hasTopContent ? undefined : [0, null, 4]}
        >
          {topContent ? (
            topContent
          ) : (
            <Image
              src="https://imgdrop.imgix.net/6715c38d9cb147288f5daa5a160a3ac2.svg"
              alt="Buying journey checkmarks"
              w="144px"
              h="24px"
            />
          )}
          <Text
            as="h2"
            fontWeight="bold"
            fontSize={hasTopContent ? 's2' : ['s2', 's3']}
            lineHeight={hasTopContent ? 's2' : ['s2', 's3']}
            mt={hasTopContent && !isMobile ? 'initial' : 5}
            mb={[showCTA ? 5 : 0, null, 0]}
          >
            {message}
          </Text>
        </Flex>
        {!showCTA && <Icon name="chevron-right" />}
        {showCTA && url && (
          <InternalLink
            analyticsName={eventName || 'journey-card'}
            aria-label={null}
            to={url}
            fontSize="s1"
            width="100%"
            maxWidth={['auto', null, '224px']}
            variant={internalLinkVariant}
            size={buttonSize || responsiveSize}
          >
            {shouldSeeAgentExperience ? 'Learn more' : 'See how it works'}
          </InternalLink>
        )}
      </Flex>
    </Box>
  );

  return showCTA ? (
    content
  ) : (
    <InternalLink
      to={url}
      target={target}
      analyticsName={eventName || 'cake-journey-card'}
      aria-label={null}
      _focus={{ boder: '1px solid brand50' }}
      borderRadius={['semiRounded', null, 'roundedSmooth']}
    >
      {content}
    </InternalLink>
  );
};
